import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Toaster as SonnerToaster } from "sonner";

import { hasPermission } from "@/features/auth/utils/permissions";
import { FeatureGate } from "@/shared/components";
import AppVersionChecker from "@/shared/components/AppVersionChecker";
import Modal from "@/shared/components/Modal";
import RemoteToastBridge from "@/shared/components/RemoteToastBridge";
import { MODULES, type ModuleKey } from "@/shared/config/modules";
import { useCapabilities } from "@/shared/hooks/useCapabilities";
import AppShell from "@/shared/layout/AppShell";
import { syncRemoteAuth } from "@/shared/lib/remoteAuthBridge";
import AuthLayout from "@/shared/layout/AuthLayout";
import ScrollToTop from "@/shared/layout/ScrollToTop";
import { useAppSelector } from "@/shared/redux/store/hooks";
import * as app from "../../import";
import { ROUTE_PATHS } from "./routePaths";

type AppRoute = {
  path: string;
  element: ReactNode;
  module?: string;
  action?: string;
  /**
   * Module flag that must be on for this edition. When it is off the route
   * behaves as if it did not exist: the sidebar hides it and a direct URL
   * lands on the user's first allowed page.
   */
  feature?: ModuleKey;
};

const publicRoutes: AppRoute[] = [{ path: ROUTE_PATHS.login.pattern, element: <app.LoginPage /> }];

// Reached from emailed links, so they must work whether or not someone is
// already signed in on this browser.
const accountRoutes: AppRoute[] = [
  { path: ROUTE_PATHS.acceptInvite.pattern, element: <app.AcceptInvitePage /> },
  { path: ROUTE_PATHS.resetPassword.pattern, element: <app.ResetPasswordPage /> },
  { path: ROUTE_PATHS.forgotPassword.pattern, element: <app.ForgotPasswordPage /> },
  // The premium payment link (Module 18): the payer is not a portal user.
  { path: ROUTE_PATHS.pay.pattern, element: <app.PayPage /> },
  // Back from the identity provider (Module 19).
  { path: ROUTE_PATHS.ssoComplete.pattern, element: <app.SsoCompletePage /> },
  // The insured's quote acceptance link (Module 20).
  { path: ROUTE_PATHS.acceptQuote.pattern, element: <app.AcceptQuotePage /> },
  // Employee self-registration for group benefits (Module 26, Phase C).
  { path: ROUTE_PATHS.benefitsActivate.pattern, element: <app.ActivateAccountPage /> },
];

// Paths come from the route registry so titles and Back navigation stay in step.
const privateRoutes: AppRoute[] = [
  {
    path: ROUTE_PATHS.dashboard.pattern,
    element: <app.Dashboard />,
    module: "dashboard",
    action: "view",
  },
  // Module 26, Phase C: an employer's HR and employees hold benefits-self grants and no dashboard
  // grant, so these come first and become their landing pages.
  {
    path: ROUTE_PATHS.employerHome.pattern,
    element: <app.EmployerHome />,
    module: "benefits-self",
    action: "manage",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerMembers.pattern,
    element: <app.EmployerMembers />,
    module: "benefits-self",
    action: "manage",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerApprovals.pattern,
    element: <app.EmployerApprovals />,
    module: "benefits-self",
    action: "approve",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerUploads.pattern,
    element: <app.EmployerUploads />,
    module: "benefits-self",
    action: "import",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerChanges.pattern,
    element: <app.EmployerChanges />,
    module: "benefits-self",
    action: "manage",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerBilling.pattern,
    element: <app.EmployerBilling />,
    module: "benefits-self",
    action: "billing",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.employerSettings.pattern,
    element: <app.EmployerSettings />,
    module: "benefits-self",
    action: "manage",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.myCover.pattern,
    element: <app.MyCover />,
    module: "benefits-self",
    action: "view",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.myFamily.pattern,
    element: <app.MyFamily />,
    module: "benefits-self",
    action: "view",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.myOptions.pattern,
    element: <app.MyOptions />,
    module: "benefits-self",
    action: "view",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.myRequests.pattern,
    element: <app.MyRequests />,
    module: "benefits-self",
    action: "view",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.myProfile.pattern,
    element: <app.MyProfile />,
    module: "benefits-self",
    action: "view",
    feature: MODULES.GROUP_BENEFITS,
  },
  { path: ROUTE_PATHS.leads.pattern, element: <app.Leads />, module: "pipeline", action: "view" },
  {
    path: ROUTE_PATHS.leadsAdd.pattern,
    element: <app.LeadAdd />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.leadEdit.pattern,
    element: <app.LeadEdit />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.leadDetail.pattern,
    element: <app.LeadDetail />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.pipelineAnalytics.pattern,
    element: <app.PipelineAnalytics />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.rfq.pattern,
    element: <app.RFQs />,
    module: "rfqs",
    action: "list",
    feature: MODULES.RFQ_TO_MARKET,
  },
  {
    path: ROUTE_PATHS.pipeline.pattern,
    element: <app.PipelineAnalytics />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.opportunities.pattern,
    element: <app.Opportunity />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.opportunitiesAdd.pattern,
    element: <app.OpportunityAdd />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.opportunityEdit.pattern,
    element: <app.OpportunityEdit />,
    module: "pipeline",
    action: "view",
  },
  {
    path: ROUTE_PATHS.policies.pattern,
    element: <app.PoliciesList />,
    module: "policies",
    action: "view",
    feature: MODULES.POLICIES,
  },
  {
    path: ROUTE_PATHS.commissions.pattern,
    element: <app.Commissions />,
    module: "money",
    action: "view",
    feature: MODULES.COMMISSIONS,
  },
  {
    path: ROUTE_PATHS.commissionsLedger.pattern,
    element: <app.CommissionsLedger />,
    module: "commission_ledger",
    action: "list",
    feature: MODULES.COMMISSIONS,
  },
  {
    path: ROUTE_PATHS.invoices.pattern,
    element: <app.Invoices />,
    module: "money",
    action: "view",
    feature: MODULES.INVOICING,
  },
  {
    path: ROUTE_PATHS.payments.pattern,
    element: <app.Payments />,
    module: "payments",
    action: "list",
    feature: MODULES.PAYMENTS,
  },
  { path: ROUTE_PATHS.tax.pattern, element: <app.Tax />, module: "money", action: "view" },
  { path: ROUTE_PATHS.formConfig.pattern, element: <app.FormConfig /> },
  // Every signed-in user manages their own password, second factor and sessions.
  {
    path: ROUTE_PATHS.accountSecurity.pattern,
    element: <app.AccountSecurityPage />,
    feature: MODULES.IDENTITY,
  },
  {
    path: ROUTE_PATHS.dataProviders.pattern,
    element: <app.DataProviders />,
    module: "data-providers",
    action: "list",
  },
  {
    path: ROUTE_PATHS.dataProviderDetail.pattern,
    element: <app.DataProviderDetail />,
    module: "data-providers",
    action: "list",
  },
  {
    path: ROUTE_PATHS.settings.pattern,
    element: <app.Settings />,
    module: "settings",
    action: "list",
  },
  { path: ROUTE_PATHS.team.pattern, element: <app.Team />, module: "users", action: "list" },
  // Insurer-edition modules; each hidden with its module flag.
  { path: ROUTE_PATHS.roles.pattern, element: <app.Roles />, module: "roles", action: "list" },
  {
    path: ROUTE_PATHS.orgProfile.pattern,
    element: <app.OrgProfile />,
    module: "org-profile",
    action: "view",
  },
  {
    path: ROUTE_PATHS.notificationTemplates.pattern,
    element: <app.NotificationTemplates />,
    module: "notifications",
    action: "list",
  },
  { path: ROUTE_PATHS.notifications.pattern, element: <app.Notifications /> },
  {
    path: ROUTE_PATHS.products.pattern,
    element: <app.Products />,
    module: "products",
    action: "list",
    feature: MODULES.PRODUCTS,
  },
  {
    path: ROUTE_PATHS.productDetail.pattern,
    element: <app.ProductDetail />,
    module: "products",
    action: "list",
    feature: MODULES.PRODUCTS,
  },
  {
    path: ROUTE_PATHS.submissions.pattern,
    element: <app.Submissions />,
    module: "submissions",
    action: "list",
    feature: MODULES.SUBMISSIONS,
  },
  {
    path: ROUTE_PATHS.submissionDetail.pattern,
    element: <app.SubmissionDetail />,
    module: "submissions",
    action: "list",
    feature: MODULES.SUBMISSIONS,
  },
  {
    path: ROUTE_PATHS.underwriting.pattern,
    element: <app.UnderwritingInbox />,
    module: "underwriting",
    action: "list",
    feature: MODULES.UNDERWRITING,
  },
  {
    path: ROUTE_PATHS.approvals.pattern,
    element: <app.Approvals />,
    module: "approvals",
    action: "list",
    feature: MODULES.UNDERWRITING,
  },
  {
    path: ROUTE_PATHS.authority.pattern,
    element: <app.AuthorityLimits />,
    module: "authority",
    action: "list",
    feature: MODULES.UNDERWRITING,
  },
  {
    path: ROUTE_PATHS.caseFile.pattern,
    element: <app.CaseFile />,
    module: "underwriting",
    action: "list",
    feature: MODULES.UNDERWRITING,
  },
  {
    path: ROUTE_PATHS.policyDetail.pattern,
    element: <app.PolicyDetail />,
    module: "policies",
    action: "view",
    feature: MODULES.POLICIES,
  },
  {
    path: ROUTE_PATHS.partners.pattern,
    element: <app.Partners />,
    module: "partners",
    action: "list",
    feature: MODULES.PARTNERS,
  },
  {
    path: ROUTE_PATHS.partnerDetail.pattern,
    element: <app.PartnerDetail />,
    module: "partners",
    action: "list",
    feature: MODULES.PARTNERS,
  },
  {
    path: ROUTE_PATHS.myOrganisation.pattern,
    element: <app.MyOrganisation />,
    feature: MODULES.PARTNERS,
  },
  // Group benefits (Module 26): staff screens.
  {
    path: ROUTE_PATHS.benefits.pattern,
    element: <app.BenefitSchemes />,
    module: "benefits",
    action: "list",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.benefitSchemeDetail.pattern,
    element: <app.BenefitSchemeDetail />,
    module: "benefits",
    action: "list",
    feature: MODULES.GROUP_BENEFITS,
  },
  {
    path: ROUTE_PATHS.commissionPlans.pattern,
    element: <app.CommissionPlans />,
    module: "commission-plans",
    action: "list",
    feature: MODULES.PAYOUTS,
  },
  {
    path: ROUTE_PATHS.payouts.pattern,
    element: <app.Payouts />,
    module: "payouts",
    action: "list",
    feature: MODULES.PAYOUTS,
  },
  {
    path: ROUTE_PATHS.statementDetail.pattern,
    element: <app.StatementDetail />,
    module: "earnings",
    action: "list",
    feature: MODULES.PAYOUTS,
  },
  {
    path: ROUTE_PATHS.earnings.pattern,
    element: <app.Earnings />,
    module: "earnings",
    action: "list",
    feature: MODULES.PAYOUTS,
  },
  {
    path: ROUTE_PATHS.rateCards.pattern,
    element: <app.RateCards />,
    module: "rate-cards",
    action: "list",
    feature: MODULES.RATING,
  },
  {
    path: ROUTE_PATHS.analytics.pattern,
    element: <app.Analytics />,
    module: "analytics",
    action: "view",
    feature: MODULES.ANALYTICS,
  },
  {
    path: ROUTE_PATHS.receivables.pattern,
    element: <app.Receivables />,
    module: "receivables",
    action: "list",
    feature: MODULES.RECEIVABLES,
  },
  {
    path: ROUTE_PATHS.bordereaux.pattern,
    element: <app.Bordereaux />,
    module: "receivables",
    action: "list",
    feature: MODULES.RECEIVABLES,
  },
  {
    path: ROUTE_PATHS.grievances.pattern,
    element: <app.Grievances />,
    module: "grievances",
    action: "list",
    feature: MODULES.COMPLIANCE,
  },
  {
    path: ROUTE_PATHS.dataExports.pattern,
    element: <app.DataExports />,
    module: "data-exports",
    action: "list",
    feature: MODULES.DATA_EXPORTS,
  },
  { path: ROUTE_PATHS.search.pattern, element: <app.SearchResults />, feature: MODULES.SEARCH },
  {
    path: ROUTE_PATHS.integrations.pattern,
    element: <app.Integrations />,
    module: "integrations",
    action: "list",
    feature: MODULES.INTEGRATIONS,
  },
  { path: ROUTE_PATHS.audit.pattern, element: <app.AuditTrail />, module: "audit", action: "list" },
  {
    path: ROUTE_PATHS.processingEngines.pattern,
    element: <app.ProcessingEngines />,
    module: "processing-engines",
    action: "list",
  },
  {
    path: ROUTE_PATHS.processingEngineDetail.pattern,
    element: <app.ProcessingEngineDetail />,
    module: "processing-engines",
    action: "list",
  },
  {
    path: ROUTE_PATHS.reportTemplates.pattern,
    element: <app.ReportTemplates />,
    module: "report-templates",
    action: "list",
    feature: MODULES.UNDERWRITING_DOSSIER,
  },
  {
    path: ROUTE_PATHS.reportTemplateDetail.pattern,
    element: <app.ReportTemplateDetail />,
    module: "report-templates",
    action: "list",
    feature: MODULES.UNDERWRITING_DOSSIER,
  },
  { path: ROUTE_PATHS.formConfigDetail.pattern, element: <app.FormConfigDetail /> },
];

export default function AppRouter() {
  const user = useAppSelector((state) => state.auth.user);
  const { isModuleEnabled } = useCapabilities();

  // Keep the NexForm remote's token mirror in step with the session, including
  // after a page reload, where the store is rehydrated without a login.
  useEffect(() => {
    syncRemoteAuth(user?.access_token ?? null);
  }, [user?.access_token]);

  const allowedPrivateRoutes = privateRoutes.filter((route) =>
    hasPermission(user?.user?.role?.permissions, route.module, route.action)
  );

  // The landing page must itself be available in this edition, or a redirect
  // from a switched-off route would bounce straight back to another one.
  const fallbackPrivatePath =
    allowedPrivateRoutes.find((route) => !route.feature || isModuleEnabled(route.feature))?.path ||
    ROUTE_PATHS.dashboard.pattern;

  const renderRoutes = (routes: AppRoute[]) =>
    routes.map(({ path, element, feature }) => (
      <Route
        key={path}
        path={path}
        element={
          feature ? (
            <FeatureGate module={feature} fallback={<Navigate to={fallbackPrivatePath} replace />}>
              {element}
            </FeatureGate>
          ) : (
            element
          )
        }
      />
    ));

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<AppShell />}>{renderRoutes(accountRoutes)}</Route>
        {user ? (
          <>
            <Route element={<AuthLayout />}>{renderRoutes(allowedPrivateRoutes)}</Route>
            <Route path="*" element={<Navigate to={fallbackPrivatePath} replace />} />
          </>
        ) : (
          <>
            <Route element={<AppShell />}>{renderRoutes(publicRoutes)}</Route>
            <Route path="*" element={<Navigate to={ROUTE_PATHS.login.pattern} replace />} />
          </>
        )}
      </Routes>
      <Modal />
      <RemoteToastBridge />
      <SonnerToaster position="bottom-right" />
      <AppVersionChecker />
    </>
  );
}
